// ═══════════════════════════════════════════════════════════════════════════════
// useAIAgentStats Hook - Fetch Viktor performance data
// ═══════════════════════════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { fetchWithAuth } from '@/lib/api-client';

export interface DateRange {
  from: Date;
  to: Date;
}

export interface AIAgentStats {
  currentVersion: {
    id: string;
    name: string;
    version: string;
    displayName: string;
    systemPrompt: string;
    isActive: boolean;
    createdAt: string;
    activatedAt: string | null;
    description: string | null;
  };
  kpis: {
    totalSessions: number;
    leadsCapured: number;
    conversionRate: number;
    avgQualificationScore: number;
    costPerLead: number;
    totalCost: number;
    sessionsDelta: number | null;
    conversionDelta: number | null;
    scoreDelta: number | null;
    costDelta: number | null;
  };
  funnel: Array<{
    state: string;
    count: number;
    percentage: number;
    dropOffRate: number;
  }>;
  leadQuality: Array<{
    range: string;
    count: number;
    percentage: number;
  }>;
  allVersions: Array<{
    id: string;
    displayName: string;
    version: string;
    isActive: boolean;
  }>;
  versions: Array<{
    id: string;
    displayName: string;
    version: string;
    isActive: boolean;
    isBestPerformer: boolean;
    conversionRate: number;
    totalSessions: number;
    createdAt: string;
  }>;
  trends: Array<{
    date: string;
    sessions: number;
    leads: number;
    conversionRate: number;
  }>;
  phase3: {
    totalConversations: number;
    successfulConversations: number;
    readyForLearning: boolean;
    conversationsNeeded: number;
    message: string;
  };
}

// Structured logging helper (dev only)
const isDev = import.meta.env.DEV;
const log = (stage: string, data?: unknown) => {
  if (isDev) {
    console.log(`[AIAgentStats] ${stage}`, data || '');
  }
};

export function useAIAgentStats(versionId?: string, dateRange?: DateRange) {
  const { session } = useAuth();
  const accessToken = session?.access_token;

  return useQuery({
    queryKey: ['ai-agent-stats', versionId, dateRange?.from?.toISOString(), dateRange?.to?.toISOString()],
    queryFn: async (): Promise<AIAgentStats> => {
      log('query:start', { versionId, hasDateRange: !!dateRange });

      if (!accessToken) {
        log('query:error', 'No access token');
        throw new Error('Not authenticated');
      }

      const params = new URLSearchParams();
      if (versionId) params.append('version_id', versionId);
      if (dateRange) {
        params.append('start_date', dateRange.from.toISOString());
        params.append('end_date', dateRange.to.toISOString());
      }

      const url = `/api/ai-agent-stats?${params}`;
      log('fetch:start', { url });

      try {
        const data = await fetchWithAuth<AIAgentStats>(url, accessToken, {
          timeout: 15000,
          retries: 1,
        });
        log('query:success', { hasData: !!data, totalSessions: data?.kpis?.totalSessions });
        return data;
      } catch (err) {
        log('query:error', err instanceof Error ? err.message : String(err));
        throw err;
      }
    },
    enabled: !!accessToken, // CRITICAL: Don't run query without token
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });
}
